import { useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getWalletOfferControls,
  updateWalletOfferControls,
  type WalletOfferControls,
} from '../api/settings';

/**
 * Hook to fetch and manage wallet-level offer controls.
 * Follows the same pattern as useNotificationPreferences with dirty tracking.
 */
export function useWalletOfferControls() {
  const queryClient = useQueryClient();

  const [localControls, setLocalControls] = useState<WalletOfferControls | null>(null);
  const [isDirty, setIsDirty] = useState(false);

  const {
    data: serverControls,
    isPending: isLoading,
    error,
    refetch,
  } = useQuery<WalletOfferControls, Error>({
    queryKey: ['walletOfferControls'],
    queryFn: getWalletOfferControls,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  // Initialize local state when server data loads
  useEffect(() => {
    if (serverControls && !isDirty) {
      setLocalControls(serverControls);
    }
  }, [serverControls, isDirty]);

  const updateMutation = useMutation<
    WalletOfferControls,
    Error,
    Partial<WalletOfferControls>
  >({
    mutationFn: updateWalletOfferControls,
    onSuccess: (data) => {
      queryClient.setQueryData(['walletOfferControls'], data);
      setLocalControls(data);
      setIsDirty(false);
    },
  });

  const updateControl = useCallback(<K extends keyof WalletOfferControls>(
    key: K,
    value: WalletOfferControls[K]
  ) => {
    setLocalControls((prev) => {
      if (!prev) return prev;
      return { ...prev, [key]: value };
    });
    setIsDirty(true);
  }, []);

  const saveControls = useCallback(() => {
    if (!localControls || !isDirty) return;
    updateMutation.mutate(localControls);
  }, [localControls, isDirty, updateMutation]);

  const resetControls = useCallback(() => {
    if (serverControls) {
      setLocalControls(serverControls);
      setIsDirty(false);
    }
  }, [serverControls]);

  return {
    controls: localControls,
    isLoading,
    isUpdating: updateMutation.isPending,
    isDirty,
    error,
    updateError: updateMutation.error,
    updateControl,
    saveControls,
    resetControls,
    refetch,
    isUpdateSuccess: updateMutation.isSuccess,
  };
}
